/**
 * Storage provider that switches between localStorage and CRDT based on workspace context
 */

import type { AppStorage } from '@yada/types';
import { CRDTStorage, type MetadataExtractor } from './crdtStorage';
import { getLegacyStorageDocumentId, getStorageDocumentId } from './documentId';
import { createLogger } from '@yada/logger';

const logger = createLogger('storageProvider');

export type ToolStorageMode = 'local' | 'synced';

const TOOL_STORAGE_KEY_PREFIX = 'yada:tool-storage:';
const GLOBAL_STORAGE_DEFAULT_PREFIX = 'yada:tool-storage-default';

function toolStorageKey(toolId: string, profileId?: string | null): string {
  return profileId
    ? `${TOOL_STORAGE_KEY_PREFIX}${profileId}:${toolId}`
    : TOOL_STORAGE_KEY_PREFIX + toolId;
}

function globalDefaultKey(profileId?: string | null): string {
  return profileId
    ? `${GLOBAL_STORAGE_DEFAULT_PREFIX}:${profileId}`
    : GLOBAL_STORAGE_DEFAULT_PREFIX;
}

/**
 * Read the global storage mode default for this profile from localStorage.
 * Returns null if no global default has been configured.
 */
export function getGlobalStorageDefault(profileId?: string | null): ToolStorageMode | null {
  if (typeof localStorage === 'undefined') return null;
  if (profileId) {
    const raw = localStorage.getItem(globalDefaultKey(profileId));
    if (raw === 'local' || raw === 'synced' || raw === 'synced-recovery') return raw as ToolStorageMode;
  }
  const fallback = localStorage.getItem(globalDefaultKey());
  if (fallback === 'local' || fallback === 'synced' || fallback === 'synced-recovery') return fallback as ToolStorageMode;
  return null;
}

/**
 * Read the per-tool, per-profile storage mode preference from localStorage.
 * Falls back to the global default if no per-tool override is set.
 * Returns null if neither a per-tool override nor a global default has been set.
 */
export function getToolStorageMode(toolId: string, profileId?: string | null): ToolStorageMode | null {
  if (typeof localStorage === 'undefined') return null;
  // Check per-tool override first
  if (profileId) {
    const raw = localStorage.getItem(toolStorageKey(toolId, profileId));
    if (raw === 'local' || raw === 'synced' || raw === 'synced-recovery') return raw as ToolStorageMode;
  }
  const rawUnprefixed = localStorage.getItem(toolStorageKey(toolId, null));
  if (rawUnprefixed === 'local' || rawUnprefixed === 'synced' || rawUnprefixed === 'synced-recovery') return rawUnprefixed as ToolStorageMode;

  // Fall back to global default (set during onboarding StorageStep)
  return getGlobalStorageDefault(profileId);
}

/**
 * Persist the per-tool, per-profile storage mode preference to localStorage.
 */
export function setToolStorageMode(toolId: string, mode: ToolStorageMode, profileId?: string | null): void {
  if (typeof localStorage === 'undefined') return;
  if (profileId) {
    localStorage.setItem(toolStorageKey(toolId, profileId), mode);
  }
  localStorage.setItem(toolStorageKey(toolId, null), mode);
}

/**
 * Extended AppStorage that exposes lock state from underlying CRDTStorage
 */
export interface HybridAppStorage extends AppStorage {
  /** Whether the storage is locked due to decryption failure */
  readonly locked: boolean;
  /** Reason the storage is locked */
  readonly lockReason: string | null;
  /**
   * Subscribe to lock state changes. Fires once when locked transitions false→true.
   * Returns an unsubscribe function.
   */
  onLock(callback: () => void): () => void;
  /**
   * Subscribe to unlock events (P2P seed delivery → real-time unlock).
   * Fires when the storage transitions locked → unlocked without a page reload.
   * Returns an unsubscribe function.
   */
  onUnlock(callback: () => void): () => void;
  /**
   * Subscribe to remote document updates. Fires whenever another device's changes
   * are merged into the local Yjs doc. Use this to reload UI state reactively.
   * Returns an unsubscribe function.
   */
  onRemoteUpdate(callback: () => void): () => void;
  /** Get depersonalized metadata (works even when locked) */
  getMetadata?(): Promise<{ itemCount: number; documentType: string } | null>;
}

// ── Global remote-update registry ─────────────────────────────────────────────
const _documentRemoteUpdateCallbacks = new Map<string, Set<() => void>>();

/**
 * Called on every `document:updated` SSE notification.
 * Fires all onRemoteUpdate() callbacks registered for the given documentId.
 */
export function fireRemoteUpdateCallbacksForDocument(documentId: string): void {
  const cbs = _documentRemoteUpdateCallbacks.get(documentId);
  if (!cbs) return;
  cbs.forEach(cb => { try { cb(); } catch { /* ignore */ } });
}

function getStoredWorkspaceId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem('yada:activeWorkspaces');
    if (raw) {
      const data = JSON.parse(raw);
      const keys = Object.keys(data);
      if (keys.length > 0) {
        return keys[0];
      }
    }
  } catch {}
  return localStorage.getItem('yada:currentWorkspaceId') || localStorage.getItem('yada:activeWorkspaceId');
}

function getStoredAppWorkspaceOverride(profileId: string, appId: string): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(`yada:app-workspace-override:${profileId}:${appId}`);
}

/**
 * Create localStorage-based storage (fallback / local mode)
 */
export function createLocalStorage(namespace: string): AppStorage {
  const prefix = `yada:${namespace}:`;
  return {
    async get<T>(key: string): Promise<T | null> {
      if (typeof localStorage === 'undefined') return null;
      const raw = localStorage.getItem(prefix + key);
      if (!raw) return null;
      try {
        return JSON.parse(raw) as T;
      } catch {
        return null;
      }
    },
    async set<T>(key: string, value: T): Promise<void> {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(prefix + key, JSON.stringify(value));
    },
    async remove(key: string): Promise<void> {
      if (typeof localStorage === 'undefined') return;
      localStorage.removeItem(prefix + key);
    },
    async keys(): Promise<string[]> {
      if (typeof localStorage === 'undefined') return [];
      const result: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix)) {
          const val = localStorage.getItem(k);
          if (val !== null && val !== 'null' && val !== 'undefined') {
            result.push(k.slice(prefix.length));
          }
        }
      }
      return result;
    },
  };
}

// Cache HybridAppStorage instances by `namespace:workspaceId:toolMode`
const _hybridStorageCache = new Map<string, HybridAppStorage>();

/**
 * Clear the HybridAppStorage instance cache. Useful on profile/workspace switch.
 */
export function clearHybridStorageCache(): void {
  _hybridStorageCache.clear();
}

/**
 * Create hybrid storage that uses CRDT when workspace is active, localStorage otherwise
 *
 * @param namespace - App ID or namespace for storage
 * @param explicitWorkspaceId - If provided, bypasses the workspace context and uses this workspace ID directly
 * @param metadataExtractor - Optional function to extract depersonalized metadata
 */
export function createHybridStorage(
  namespace: string,
  explicitWorkspaceId?: string | MetadataExtractor,
  metadataExtractor?: MetadataExtractor
): HybridAppStorage {
  let resolvedWorkspaceIdOverride: string | undefined;
  let resolvedMetadataExtractor: MetadataExtractor | undefined;
  if (typeof explicitWorkspaceId === 'string') {
    resolvedWorkspaceIdOverride = explicitWorkspaceId;
    resolvedMetadataExtractor = metadataExtractor;
  } else {
    resolvedMetadataExtractor = explicitWorkspaceId;
  }

  const profileId = typeof localStorage !== 'undefined'
    ? localStorage.getItem('yada:account:activeProfileId')
    : null;

  const toolMode = getToolStorageMode(namespace, profileId);

  // If the user explicitly chose local-only for this tool, honour it regardless of workspace state
  if (toolMode === 'local' && !resolvedWorkspaceIdOverride) {
    const cacheKey = `local:${namespace}:${profileId || 'default'}`;
    const cached = _hybridStorageCache.get(cacheKey);
    if (cached) return cached;

    logger.log(`Using localStorage (tool mode: local) for ${namespace}`);
    const local = createLocalStorage(namespace);
    const inst: HybridAppStorage = { ...local, locked: false, lockReason: null, onLock: () => () => {}, onUnlock: () => () => {}, onRemoteUpdate: () => () => {} };
    _hybridStorageCache.set(cacheKey, inst);
    return inst;
  }

  // Community votes is public and server-backed — never tie it to workspace CRDT sync
  if (namespace === '@yada/app-community-votes' && !resolvedWorkspaceIdOverride) {
    const cacheKey = `public:${namespace}`;
    const cached = _hybridStorageCache.get(cacheKey);
    if (cached) return cached;

    logger.log(`Using localStorage (public tool) for ${namespace}`);
    const local = createLocalStorage(namespace);
    const inst: HybridAppStorage = { ...local, locked: false, lockReason: null, onLock: () => () => {}, onUnlock: () => () => {}, onRemoteUpdate: () => () => {} };
    _hybridStorageCache.set(cacheKey, inst);
    return inst;
  }

  const appOverride = (!resolvedWorkspaceIdOverride && profileId)
    ? getStoredAppWorkspaceOverride(profileId, namespace)
    : null;

  const workspaceId = resolvedWorkspaceIdOverride ?? appOverride ?? getStoredWorkspaceId();

  logger.log(`createHybridStorage(${namespace}) - workspaceId: ${workspaceId}, profileId: ${profileId}, toolMode: ${toolMode}`);

  if (!workspaceId || !profileId) {
    const cacheKey = `local-fallback:${namespace}:${profileId || 'none'}:${workspaceId || 'none'}`;
    const cached = _hybridStorageCache.get(cacheKey);
    if (cached) return cached;

    const missing = [!workspaceId && 'workspaceId', !profileId && 'profileId'].filter(Boolean).join(', ');
    logger.warn(`⚠️ Falling back to browser localStorage for "${namespace}" — missing: ${missing}. Data will NOT sync across devices.`);
    const localNamespace = resolvedWorkspaceIdOverride ? `${namespace}:${resolvedWorkspaceIdOverride}` : namespace;
    const local = createLocalStorage(localNamespace);
    const inst: HybridAppStorage = { ...local, locked: false, lockReason: null, onLock: () => () => {}, onUnlock: () => () => {}, onRemoteUpdate: () => () => {} };
    _hybridStorageCache.set(cacheKey, inst);
    return inst;
  }

  const cacheKey = `crdt:${namespace}:${workspaceId}`;
  const cached = _hybridStorageCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  logger.log(`Using CRDT storage for workspace ${workspaceId}`);
  const crdtStorage = new CRDTStorage(
    workspaceId,
    getStorageDocumentId(namespace, workspaceId),
    namespace,
    namespace,
    resolvedMetadataExtractor,
    getLegacyStorageDocumentId(namespace, workspaceId)
  );

  const hybridStorage: HybridAppStorage = {
    get locked() { return crdtStorage.locked; },
    get lockReason() { return crdtStorage.lockReason; },
    onLock(callback: () => void) { return crdtStorage.onLock(callback); },
    onUnlock(callback: () => void) { return crdtStorage.onUnlock(callback); },
    onRemoteUpdate(callback: () => void) {
      const unsubCrdt = crdtStorage.onRemoteUpdate(callback);
      const documentIds = [
        getStorageDocumentId(namespace, workspaceId),
        getLegacyStorageDocumentId(namespace, workspaceId),
      ];
      for (const docId of documentIds) {
        if (!_documentRemoteUpdateCallbacks.has(docId)) {
          _documentRemoteUpdateCallbacks.set(docId, new Set());
        }
        _documentRemoteUpdateCallbacks.get(docId)!.add(callback);
      }
      return () => {
        unsubCrdt();
        for (const docId of documentIds) {
          _documentRemoteUpdateCallbacks.get(docId)?.delete(callback);
        }
      };
    },
    getMetadata: () => crdtStorage.getMetadata(),
    async get<T>(key: string): Promise<T | null> {
      if (!crdtStorage.getDocument() && !crdtStorage.locked) {
        logger.log(`Initializing CRDT storage for get(${key})`);
        await crdtStorage.initialize();
        logger.log('CRDT storage initialized');
      }
      return crdtStorage.get<T>(key);
    },
    async set<T>(key: string, value: T): Promise<void> {
      if (!crdtStorage.getDocument() && !crdtStorage.locked) {
        logger.log(`Initializing CRDT storage for set(${key})`);
        await crdtStorage.initialize();
        logger.log('CRDT storage initialized');
      }
      return crdtStorage.set<T>(key, value);
    },
    async remove(key: string): Promise<void> {
      if (!crdtStorage.getDocument() && !crdtStorage.locked) {
        logger.log(`Initializing CRDT storage for remove(${key})`);
        await crdtStorage.initialize();
        logger.log('CRDT storage initialized');
      }
      return crdtStorage.remove(key);
    },
    async keys(): Promise<string[]> {
      if (!crdtStorage.getDocument() && !crdtStorage.locked) {
        logger.log('Initializing CRDT storage for keys()');
        await crdtStorage.initialize();
        logger.log('CRDT storage initialized');
      }
      return crdtStorage.keys();
    },
  };

  _hybridStorageCache.set(cacheKey, hybridStorage);
  return hybridStorage;
}
